/**
 * DoctrineAccessModal — two-step modal for the "Only for Design and
 * Development team" gate:
 *   1. Name + Department form (recorded server-side for audit).
 *   2. Once submitted, the same modal swaps to a full PDF view of the
 *      UniVicoustic Product Design Doctrine.
 *
 * The PDF stays open as long as the user wants; closing the modal returns
 * them to the learner home unchanged.
 */
import React, { useEffect, useState } from "react";
import { Lock, X } from "lucide-react";

import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PDFViewer } from "./PDFViewer";

export function DoctrineAccessModal({ open, onClose }) {
  const [step, setStep] = useState("form"); // form | pdf
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Lock body scroll while open + reset state on close.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Reset on close so the next opener sees a fresh form.
      setStep("form");
      setName("");
      setDepartment("");
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    const n = name.trim();
    const d = department.trim();
    if (!n || !d) {
      setError("Both fields are required.");
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await api.post("/gates/doctrine/access", { name: n, department: d });
      setStep("pdf");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not log access. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[900] bg-[#222222]/90 backdrop-blur-sm flex items-stretch justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="doctrine-title"
      data-testid="doctrine-modal"
    >
      <div className="w-full max-w-4xl m-4 bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-[#222222] to-[#1a1a1a] text-white flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Lock className="text-[#E53935]" size={22} />
              <h2 id="doctrine-title" className="text-lg font-semibold tracking-tight">
                Product Design Doctrine — Design &amp; Development team
              </h2>
            </div>
            <p className="text-sm text-slate-300 mt-1">
              {step === "form"
                ? "Please enter your name and department. We log every access for audit."
                : "Confidential — UniVicoustic internal Design & Development reference."}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-white"
            aria-label="Close"
            data-testid="doctrine-close"
          >
            <X size={20}/>
          </button>
        </div>

        {step === "form" ? (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="doctrine-form">
            <div>
              <Label htmlFor="d-name" className="text-sm">Full name</Label>
              <Input
                id="d-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Priya Sharma"
                data-testid="doctrine-name"
                autoFocus
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="d-dept" className="text-sm">Department</Label>
              <Input
                id="d-dept"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g. Design, Product, Engineering"
                data-testid="doctrine-department"
                className="mt-1"
              />
            </div>
            {error && (
              <div className="text-xs text-rose-700" data-testid="doctrine-error">{error}</div>
            )}
            <div className="text-xs text-slate-500 leading-relaxed">
              By proceeding you confirm you are accessing this document in your
              capacity as part of the UniVicoustic Design or Development function.
              Your access timestamp is recorded.
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="doctrine-cancel">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !name.trim() || !department.trim()}
                data-testid="doctrine-submit"
                className="bg-[#E53935] hover:bg-[#c62828] text-white disabled:opacity-50"
              >
                {submitting ? "Verifying…" : "Open doctrine"}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex-1 min-h-0">
            <PDFViewer
              src="/api/gates/doctrine.pdf"
              ariaLabel="UniVicoustic Product Design Doctrine"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DoctrineAccessModal;
